import { ASTCall } from '../ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType } from '../expr_types';
import {
  assertArgShape,
  assertKwargShape,
  assertNumeric,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { assertString, evalExpression } from '../eval';

import { parseUnitString } from '../../unit';
import { exprToSql } from '../sql';
import { Quantity } from '../quantity';
import { extractStringLiteral } from '../literal';
import { traceHelper } from '../sql/internal';
import { TraceASTConversion, TraceASTNumber, TraceASTType } from '../trace';

// Function for casting a number into a unit. The syntax is:
// cast(expr, "unit name")
export const cast: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 2);
    assertKwargShape(call, [], false);

    assertNumeric(ctx, call.args[0], warnings);

    const unitString = extractStringLiteral(call.args[1]);
    const unit = parseUnitString(assertString(unitString));
    return { ty: 'number', unit };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const v = evalExpression(ctx, call.args[0]);
    if (v instanceof Quantity) {
      // Intentionally throw away the unit
      return v.value;
    } else {
      return v;
    }
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const unitString = extractStringLiteral(call.args[1]);
    const unit = parseUnitString(assertString(unitString));

    // Common case: casting a number literal to a unit. Just pretend the number
    // literal had that unit to begin with!
    // TODO: this "common case" should be easier to write
    let n: number | undefined = undefined;
    if (
      call.args[0].t === 'unary' &&
      call.args[0].op === '-' &&
      call.args[0].expr.t === 'number'
    ) {
      n = -call.args[0].expr.v;
    } else if (call.args[0].t === 'number') {
      n = call.args[0].v;
    }
    if (n !== undefined) {
      return traceHelper<TraceASTNumber>(
        {
          ty: 'number',
          unit,
        },
        {
          expr: `CAST(${n.toString()} AS DOUBLE)`,
          stage: 0,
          unitExpr: null,
          error: null,
        },
        () => ({ t: TraceASTType.TNumber }),
        { valueMattersInFingerprint: true }
      );
    }

    const ty = typecheckExpression(ctx, call);
    const eSql = exprToSql(ctx, call.args[0]);
    return traceHelper<TraceASTConversion>(
      ty,
      {
        expr: eSql.expr,
        stage: eSql.stage,
        unitExpr: null,
        error: eSql.error,
      },
      (sub) => ({
        t: TraceASTType.TConversion,
        e: sub(eSql.trace),
        d: 'unit cast (override)',
      })
    );
  },
};
